import React from 'react'
import { Paper, Grid, Box, IconButton, TextField, Autocomplete, Select, MenuItem, FormControl, InputLabel, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

export default function EmployeeCard({ emp, index, employees, form, setForm, langIndex, greenTheme, inputStyles, inputLabelStyles, selectStyles, group, weekDays, input, isOrdered }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ 
    id: emp.id || index, 
    disabled: !isOrdered 
  })

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <Paper 
      ref={setNodeRef}
      style={style}
      sx={{ 
        p: 2, 
        mb: 2, 
        border: `1px solid ${greenTheme.primary}`, 
        backgroundColor: '#f9fafb',
        position: 'relative',
        zIndex: isDragging ? 1000 : 1,
      }}
    >
      <Grid container spacing={3}>
        {isOrdered && (
          <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box
              component="span"
              {...attributes}
              {...listeners}
              sx={{
                cursor: isOrdered ? 'grab' : 'default',
                '&:active': {
                  cursor: 'grabbing',
                },
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                touchAction: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            >
              <IconButton
                size="small"
                sx={{
                  color: greenTheme.primary,
                  pointerEvents: 'none',
                }}
                aria-label="Drag to reorder"
              >
                <DragIndicatorIcon />
              </IconButton>
            </Box>
          </Grid>
        )}
        <Grid 
          item 
          xs={isOrdered ? (form.employees.length > 1 ? 10 : 11) : (form.employees.length > 1 ? 11 : 12)} 
          sm={isOrdered ? (form.employees.length > 1 ? 10 : 11) : (form.employees.length > 1 ? 11 : 12)}
          onClick={(e) => {
            if (isOrdered) {
              e.stopPropagation()
            }
          }}
        >
          <Autocomplete
            options={employees}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option
              return option.label || option.buddy || ''
            }}
            value={employees.find(e => e.buddy === emp.employee) || null}
            onChange={(event, newValue) => {
              const updatedEmployees = [...form.employees]
              updatedEmployees[index].employee = newValue ? newValue.buddy : ''
              setForm({ ...form, employees: updatedEmployees })
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={group.employee_label[langIndex]}
                placeholder={group.employee_placeholder[langIndex]}
                size="small"
                sx={inputStyles}
              />
            )}
          />
        </Grid>
        {form.employees.length > 1 && (
          <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <IconButton
              size="small"
              onClick={() => {
                const updatedEmployees = form.employees.filter((_, i) => i !== index)
                setForm({ ...form, employees: updatedEmployees })
              }}
              sx={{
                color: '#ef4444',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                },
              }}
              aria-label="Remove employee"
            >
              <DeleteIcon fontSize="medium" />
            </IconButton>
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel sx={inputLabelStyles}>
              {group.start_days[langIndex]}
            </InputLabel>
            <Select
              size="small"
              value={emp.startDay || ''}
              onChange={(e) => {
                const updatedEmployees = [...form.employees]
                updatedEmployees[index].startDay = e.target.value
                setForm({ ...form, employees: updatedEmployees })
              }}
              label={group.start_days[langIndex]}
              sx={selectStyles}
            >
              <MenuItem value="">
                <em>{input.select[langIndex]}</em>
              </MenuItem>
              {weekDays.map((day, dayIndex) => (
                <MenuItem key={dayIndex} value={dayIndex + 1}>
                  {day[langIndex]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel sx={inputLabelStyles}>
              {group.end_days[langIndex]}
            </InputLabel>
            <Select
              size="small"
              value={emp.endDay || ''}
              onChange={(e) => {
                const updatedEmployees = [...form.employees]
                updatedEmployees[index].endDay = e.target.value
                setForm({ ...form, employees: updatedEmployees })
              }}
              label={group.end_days[langIndex]}
              sx={selectStyles}
            >
              <MenuItem value="">
                <em>{input.select[langIndex]}</em>
              </MenuItem>
              {weekDays.map((day, dayIndex) => (
                <MenuItem key={dayIndex} value={dayIndex + 1}>
                  {day[langIndex]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" sx={{ minWidth: '80px', fontWeight: 500, color: '#64748b' }}>
              {group.time_start[langIndex]}:
            </Typography>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                size="small"
                value={emp.startHour}
                onChange={(e) => {
                  const updatedEmployees = [...form.employees]
                  updatedEmployees[index].startHour = e.target.value
                  setForm({ ...form, employees: updatedEmployees })
                }}
                displayEmpty
                sx={selectStyles}
              >
                <MenuItem value=""><em>HH</em></MenuItem>
                {Array.from({ length: 24 }, (_, i) => (
                  <MenuItem key={i} value={String(i).padStart(2, '0')}>
                    {String(i).padStart(2, '0')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" sx={{ mx: 0.5 }}>:</Typography>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                size="small"
                value={emp.startMinute}
                onChange={(e) => {
                  const updatedEmployees = [...form.employees]
                  updatedEmployees[index].startMinute = e.target.value
                  setForm({ ...form, employees: updatedEmployees })
                }}
                displayEmpty
                sx={selectStyles}
              >
                <MenuItem value=""><em>MM</em></MenuItem>
                {Array.from({ length: 60 }, (_, i) => (
                  <MenuItem key={i} value={String(i).padStart(2, '0')}>
                    {String(i).padStart(2, '0')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" sx={{ minWidth: '80px', fontWeight: 500, color: '#64748b' }}>
              {group.time_end[langIndex]}:
            </Typography>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                size="small"
                value={emp.endHour}
                onChange={(e) => {
                  const updatedEmployees = [...form.employees]
                  updatedEmployees[index].endHour = e.target.value
                  setForm({ ...form, employees: updatedEmployees })
                }}
                displayEmpty
                sx={selectStyles}
              >
                <MenuItem value=""><em>HH</em></MenuItem>
                {Array.from({ length: 24 }, (_, i) => (
                  <MenuItem key={i} value={String(i).padStart(2, '0')}>
                    {String(i).padStart(2, '0')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" sx={{ mx: 0.5 }}>:</Typography>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                size="small"
                value={emp.endMinute}
                onChange={(e) => {
                  const updatedEmployees = [...form.employees]
                  updatedEmployees[index].endMinute = e.target.value
                  setForm({ ...form, employees: updatedEmployees })
                }}
                displayEmpty
                sx={selectStyles}
              >
                <MenuItem value=""><em>MM</em></MenuItem>
                {Array.from({ length: 60 }, (_, i) => (
                  <MenuItem key={i} value={String(i).padStart(2, '0')}>
                    {String(i).padStart(2, '0')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  )
}
